import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// ── SVG icon component ──────────────────────────────────────────────────────
const NavIcon = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.75"
    strokeLinecap="round" strokeLinejoin="round">
    {(Array.isArray(d) ? d : [d]).map((path, i) => <path key={i} d={path} />)}
  </svg>
);

// ── Feather-style icon paths ────────────────────────────────────────────────
const IC = {
  dashboard: ['M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z', 'M9 22V12h6v10'],
  tables:    ['M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z'],
  entities:  ['M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2', 'M9 11a4 4 0 100-8 4 4 0 000 8z', 'M23 21v-2a4 4 0 00-3-3.87', 'M16 3.13a4 4 0 010 7.75'],
  rq:        ['M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8', 'M10 9H8'],
  oc:        ['M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z', 'M3 6h18', 'M16 10a4 4 0 01-8 0'],
  almacen:   ['M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z', 'M3.27 6.96L12 12.01l8.73-5.05', 'M12 22.08V12'],
  kardex:    ['M18 20V10', 'M12 20V4', 'M6 20v-6'],
  cobranzas: ['M12 1v22', 'M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6'],
  pagos:     ['M21 4H3a2 2 0 00-2 2v12a2 2 0 002 2h18a2 2 0 002-2V6a2 2 0 00-2-2z', 'M1 10h22'],
  contab:    ['M4 4h16v16H4z', 'M9 4v16', 'M4 9h5', 'M4 14h5', 'M4 19h5', 'M12 8h5', 'M12 12h5', 'M12 16h5'],
  settings:  ['M12 15a3 3 0 100-6 3 3 0 000 6z', 'M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z'],
  list:      ['M8 6h13', 'M8 12h13', 'M8 18h13', 'M3 6h.01', 'M3 12h.01', 'M3 18h.01'],
  check:     ['M22 11.08V12a10 10 0 11-5.93-9.14', 'M22 4L12 14.01l-3-3'],
  link:      ['M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71', 'M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71'],
  exchange:  ['M7 16V4m0 0L3 8m4-4l4 4', 'M17 8v12m0 0l4-4m-4 4l-4-4'],
  tag:       ['M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z', 'M7 7h.01'],
  layers:    ['M12 2L2 7l10 5 10-5-10-5z', 'M2 17l10 5 10-5', 'M2 12l10 5 10-5'],
  grid:      ['M3 3h7v7H3z', 'M14 3h7v7h-7z', 'M3 14h7v7H3z', 'M14 14h7v7h-7z'],
  truck:     ['M1 3h15v13H1z', 'M16 8h4l3 3v5h-7V8z', 'M5.5 21a2.5 2.5 0 100-5 2.5 2.5 0 000 5z', 'M18.5 21a2.5 2.5 0 100-5 2.5 2.5 0 000 5z'],
  building:  ['M3 21h18', 'M9 21V7l3-3 3 3v14', 'M5 21V10.5'],
  user:      ['M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2', 'M12 11a4 4 0 100-8 4 4 0 000 8z'],
  map:       ['M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z', 'M12 10a1 1 0 100-2 1 1 0 000 2z'],
  dollar:    ['M12 1v22', 'M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6'],
  ruler:     ['M21.3 15.3l-9.6-9.6a2 2 0 00-2.82 0l-2.82 2.82a2 2 0 000 2.82l9.6 9.6a2 2 0 002.82 0l2.82-2.82a2 2 0 000-2.82z', 'M6 6l1.5 1.5', 'M9 9l1.5 1.5'],
  chart:     ['M22 12h-4l-3 9L9 3l-3 9H2'],
  inbox:     ['M22 12h-6l-2 3h-4l-2-3H2', 'M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z'],
  arrow:     ['M5 12h14', 'M12 5l7 7-7 7'],
};

// ── Menu definition ─────────────────────────────────────────────────────────
const menuItems = [
  { section: 'Principal' },
  { name: 'Dashboard',      icon: 'dashboard', path: '/dashboard' },

  { section: 'Maestros' },
  {
    name: 'Tablas Generales', icon: 'tables',
    submenu: [
      { name: 'Artículos',        icon: 'tag',      path: '/tablas/articulos' },
      { name: 'Unid. de Medida',  icon: 'ruler',    path: '/tablas/unidades-medida' },
      { name: 'Centros de Costo', icon: 'building', path: '/tablas/centros-costo' },
      { name: 'Tipo de Cambio',   icon: 'exchange', path: '/tablas/tipo-cambio' },
      { name: 'Categorías',       icon: 'grid',     path: '/tablas/categorias' },
      { name: 'Marcas',           icon: 'tag',      path: '/tablas/marcas' },
      { name: 'Modelos',          icon: 'layers',   path: '/tablas/modelos' },
      { name: 'Familias',         icon: 'layers',   path: '/tablas/familias' },
      { name: 'Líneas',           icon: 'list',     path: '/tablas/lineas' },
      { name: 'Grupos',           icon: 'layers',   path: '/tablas/grupos' },
      { name: 'Tipo de Existencia', icon: 'grid',   path: '/tablas/tipos-existencia' },
    ],
  },
  {
    name: 'Entidades', icon: 'entities',
    submenu: [
      { name: 'Prov. / Clientes', icon: 'building', path: '/entidades/proveedores-clientes' },
      { name: 'Trabajadores',     icon: 'user',     path: '/entidades/trabajadores' },
      { name: 'Transportistas',   icon: 'truck',    path: '/entidades/transportistas' },
      { name: 'Vehículos',        icon: 'truck',    path: '/entidades/vehiculos' },
    ],
  },

  { section: 'Logística' },
  {
    name: 'Requerimientos', icon: 'rq',
    submenu: [
      { name: 'Listar', icon: 'list', path: '/requerimientos/listar' },
    ],
  },
  {
    name: 'Orden de Compra', icon: 'oc',
    submenu: [
      { name: 'Listar',    icon: 'list',  path: '/orden-compra/listar' },
      { name: 'OC desde RQ', icon: 'link', path: '/orden-compra/desde-rq' },
    ],
  },

  { section: 'Almacén' },
  {
    name: 'Parte de Entrada', icon: 'inbox',
    submenu: [
      { name: 'Parte de Entrada',    icon: 'inbox',  path: '/almacen/parte-entrada/listar' },
      { name: 'PE Valorizado',       icon: 'dollar', path: '/almacen/pe-valorizado' },
      { name: 'PE desde OC',         icon: 'link',   path: '/almacen/pe-desde-oc' },
      { name: 'PE desde GR',         icon: 'layers', path: '/almacen/pe-desde-gr' },
    ],
  },
  {
    name: 'Parte de Salida', icon: 'arrow',
    submenu: [
      { name: 'Parte de Salida',       icon: 'arrow',  path: '/almacen/parte-salida' },
      { name: 'PS Valorizada',         icon: 'dollar', path: '/almacen/ps-valorizada' },
      { name: 'PS desde PE',           icon: 'link',   path: '/almacen/ps-desde-pe' },
      { name: 'PS desde Requerimiento',icon: 'rq',     path: '/almacen/ps-desde-rq' },
    ],
  },
  {
    name: 'Guía de Remisión', icon: 'truck',
    submenu: [
      { name: 'Guías de Remisión', icon: 'list',   path: '/almacen/guias-remision' },
      { name: 'GR desde PE',       icon: 'link',   path: '/almacen/gr-desde-pe' },
      { name: 'GR desde RQ',       icon: 'rq',     path: '/almacen/gr-desde-rq' },
    ],
  },
  {
    name: 'Kardex', icon: 'kardex',
    submenu: [
      { name: 'Movimientos',        icon: 'list', path: '/kardex/movimientos' },
      { name: 'Reporte de Consumo', icon: 'list', path: '/kardex/consumo' },
    ],
  },

  { section: 'Finanzas' },
  {
    name: 'Cobranzas', icon: 'cobranzas',
    submenu: [
      { name: 'Cuentas por Cobrar', icon: 'list',  path: '/cobranzas/cuentas' },
      { name: 'Registro de Cobros', icon: 'check', path: '/cobranzas/registro' },
    ],
  },
  {
    name: 'Pagos', icon: 'pagos',
    submenu: [
      { name: 'Cuentas por Pagar',  icon: 'list',  path: '/pagos/cuentas' },
      { name: 'Registro de Pagos',  icon: 'check', path: '/pagos/registro' },
    ],
  },
  {
    name: 'Contabilidad', icon: 'contab',
    submenu: [
      { name: 'Cuentas Contables', icon: 'list',     path: '/contabilidad/cuentas-contables' },
      { name: 'Años Contables',    icon: 'exchange', path: '/contabilidad/siscont-anios' },
    ],
  },

  { section: 'Sistema' },
  {
    name: 'Utilitarios', icon: 'settings',
    submenu: [
      { name: 'Sucursales',           icon: 'building', path: '/utilitarios/sucursales' },
      { name: 'Almacenes',            icon: 'almacen',  path: '/utilitarios/almacenes' },
      { name: 'Ubigeos',              icon: 'map',      path: '/utilitarios/ubigeos' },
      { name: 'Series',               icon: 'list',     path: '/utilitarios/series' },
      { name: 'Tipo Doc. Serie',      icon: 'layers',   path: '/utilitarios/series-modulos' },
      { name: 'Tipo Doc. Comerciales',icon: 'tag',      path: '/utilitarios/tipos-documento' },
      { name: 'Tipos de Movimiento',  icon: 'exchange', path: '/utilitarios/tipos-movimiento' },
      { name: 'Tipos de Operación',   icon: 'grid',     path: '/utilitarios/tipos-operacion' },
      { name: 'Motivos de Traslado',  icon: 'arrow',    path: '/utilitarios/motivos-traslado' },
      { name: 'Formas de Pago',       icon: 'pagos',    path: '/utilitarios/formas-pago' },
      { name: 'Areas',                icon: 'grid',     path: '/utilitarios/areas' },
      { name: 'Cargos',               icon: 'user',     path: '/utilitarios/cargos' },
      { name: 'Prioridades',          icon: 'arrow',    path: '/utilitarios/prioridades' },
      { name: 'Empresas',             icon: 'building', path: '/utilitarios/empresas',        adminOnly: true },
      { name: 'Datos Generales',      icon: 'building', path: '/utilitarios/datos-generales', adminOnly: true },
      { name: 'Usuarios',             icon: 'entities', path: '/utilitarios/usuarios',        adminOnly: true },
    ],
  },
];

// ── Helpers ─────────────────────────────────────────────────────────────────
const getActiveParentName = (items, pathname) => {
  const parent = items.find(
    item => item.submenu && item.submenu.some(s => pathname.startsWith(s.path))
  );
  return parent ? parent.name : null;
};

// Filtra el menu segun el perfil. Quita items adminOnly y, si un grupo queda
// sin submenu, tambien lo oculta.
const filterMenu = (items, isAdmin) => {
  if (isAdmin) return items;
  return items
    .map(item => {
      if (item.submenu) {
        const sub = item.submenu.filter(s => !s.adminOnly);
        if (sub.length === 0) return null;
        return { ...item, submenu: sub };
      }
      if (item.adminOnly) return null;
      return item;
    })
    .filter(Boolean);
};

const Sidebar = ({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user }  = useAuth();
  const isAdmin   = user?.id_perfil === 1;
  const visibleMenu = useMemo(() => filterMenu(menuItems, isAdmin), [isAdmin]);
  const [openMenu, setOpenMenu] = useState(() => getActiveParentName(visibleMenu, location.pathname));
  const [hovered, setHovered]   = useState(false);
  const sidebarRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (collapsed && hovered && sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setHovered(false);
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [collapsed, hovered]);

  const handleMouseEnter = () => { if (collapsed) setHovered(true); };
  const handleMouseLeave = () => {
    if (collapsed) { setHovered(false); setOpenMenu(null); }
  };
  const toggleSubmenu = (name) => setOpenMenu(prev => prev === name ? null : name);
  const handleNav = (path) => {
    navigate(path);
    setMobileOpen(false);
    if (collapsed) setHovered(false);
  };
  const isActive = (item) => {
    if (item.path)    return location.pathname === item.path;
    if (item.submenu) return item.submenu.some(s => location.pathname.startsWith(s.path));
    return false;
  };

  const isExpanded = !collapsed || hovered;
  const isFloating = collapsed && hovered;

  return (
    <>
      <aside
        ref={sidebarRef}
        className={[
          'sidebar',
          collapsed && !hovered ? 'collapsed' : '',
          isFloating ? 'floating' : '',
          mobileOpen ? 'mobile-open' : '',
        ].filter(Boolean).join(' ')}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Header */}
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
            </svg>
          </div>
          {isExpanded && (
            <div className="sidebar-brand">
              <h2>Siscorp</h2>
              <p>Almacen</p>
            </div>
          )}
        </div>

        <button
          className="sidebar-toggle"
          onClick={() => { setCollapsed(!collapsed); setHovered(false); setOpenMenu(null); }}
        >
          {collapsed ? '▸' : '◂'}
        </button>

        {/* Nav */}
        <nav className="sidebar-nav">
          {visibleMenu.map((item, idx) => {
            // ── Section label ──
            if (item.section) {
              return isExpanded ? (
                <div key={`sec-${idx}`} className="nav-section-label">{item.section}</div>
              ) : (
                <div key={`sec-${idx}`} className="nav-section-divider" />
              );
            }

            // ── Item with submenu ──
            if (item.submenu) {
              return (
                <div key={item.name} className="nav-group">
                  <div
                    className={`nav-item ${isActive(item) ? 'active' : ''}`}
                    onClick={() => toggleSubmenu(item.name)}
                  >
                    <span className="nav-item-icon">
                      <NavIcon d={IC[item.icon] || IC.list} />
                    </span>
                    {isExpanded && <span className="nav-item-text">{item.name}</span>}
                    {isExpanded && (
                      <span className={`nav-item-arrow ${openMenu === item.name ? 'rotated' : ''}`}>‹</span>
                    )}
                  </div>
                  {isExpanded && (
                    <div className={`nav-submenu ${openMenu === item.name ? 'open' : ''}`}>
                      {item.submenu.map((sub) => (
                        <a
                          key={sub.path}
                          href={sub.path}
                          className={`nav-item sub ${location.pathname === sub.path ? 'active' : ''}`}
                          onClick={(e) => {
                            if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
                            e.preventDefault();
                            handleNav(sub.path);
                          }}
                        >
                          <span className="nav-item-icon sub-icon">
                            <NavIcon d={IC[sub.icon] || IC.list} size={14} />
                          </span>
                          <span className="nav-item-text">{sub.name}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            // ── Simple item ──
            return (
              <div key={item.name} className="nav-group">
                <a
                  href={item.path}
                  className={`nav-item ${isActive(item) ? 'active' : ''}`}
                  onClick={(e) => {
                    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
                    e.preventDefault();
                    handleNav(item.path);
                    setOpenMenu(null);
                  }}
                >
                  <span className="nav-item-icon">
                    <NavIcon d={IC[item.icon] || IC.list} />
                  </span>
                  {isExpanded && <span className="nav-item-text">{item.name}</span>}
                </a>
              </div>
            );
          })}
        </nav>
      </aside>

      {mobileOpen && (
        <div className="sidebar-overlay visible" />
      )}
    </>
  );
};

export default Sidebar;
